import { Toast as HotToast, toast as hotToast } from 'react-hot-toast';
import Button from "./Button";

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastProps {
  /** The react-hot-toast handle — carries visibility and the id used to dismiss. */
  t: HotToast;
  variant: ToastVariant;
  message: string;
  /** Offers a way back from the action the toast is reporting, e.g. Undo. */
  action?: { label: string; onClick: () => void };
}

/**
 * Per-variant presentation. Colors come from the semantic palette in
 * docs/product_website/ui-design.md (badges and status only) — the brand red gradient is
 * reserved for primary actions, so an error toast uses semantic red, not it.
 */
const VARIANTS: Record<
  ToastVariant,
  { iconWrap: string; icon: string; path: string; label: string }
> = {
  success: {
    iconWrap: 'bg-green-100 border border-green-200',
    icon: 'text-green-700',
    path: 'M4.5 12.75l6 6 9-13.5',
    label: 'Success',
  },
  error: {
    iconWrap: 'bg-red-100 border border-red-200',
    icon: 'text-red-700',
    path: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
    label: 'Error',
  },
  info: {
    iconWrap: 'bg-blue-100 border border-blue-200',
    icon: 'text-blue-700',
    path: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.852l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
    label: 'Note',
  },
};

/**
 * Vein's toast. Rendered through `toast.custom` (see src/lib/toast.ts), so
 * react-hot-toast still owns queueing, positioning, and timing while the card
 * itself is ours: glassmorphism and `rounded-xl` per docs/product_website/ui-design.md rather
 * than the library's default pill.
 *
 * Errors announce assertively and successes politely — an error interrupts
 * because the user's action did not take effect.
 */
export const Toast = ({ t, variant, message, action }: ToastProps) => {
  const v = VARIANTS[variant];
  const isError = variant === 'error';

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={`
        flex w-fit max-w-sm items-start gap-2.5 p-3
        bg-white border border-vein-border rounded-xl shadow-lg
        transition-all duration-300
        ${t.visible ? 'animate-fade-up' : 'opacity-0 translate-y-1'}
      `}
    >
      <span
        className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg ${v.iconWrap}`}
      >
        <svg
          className={`w-4 h-4 ${v.icon}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={v.path} />
        </svg>
      </span>

      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm text-gray-900 font-medium break-words">
          <span className="sr-only">{v.label}: </span>
          {message}
        </p>
        {action && (
          <Button
            variant="bare"
            size="sm"
            type="button"
            className="mt-1 -ml-1 px-1 text-sm font-semibold text-vein-red-500 hover:text-vein-red-600"
            onClick={() => {
              action.onClick();
              hotToast.dismiss(t.id);
            }}
          >
            {action.label}
          </Button>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={() => hotToast.dismiss(t.id)}
        aria-label="Dismiss notification"
        className="flex-shrink-0"
        icon={
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        }
      />
    </div>
  );
};
